import { useEffect, useState, useCallback, useRef } from "react";

export function useAsync<T>(
  callback: (...args: any[]) => Promise<T>,
  key: string,
  { skip = false }: { skip?: boolean } = {}
) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasBeenCalled, setHasBeenCalled] = useState<boolean>(false);
  const [lastKey, setLastKey] = useState<string>();

  const callbackRef = useRef(callback);

  callbackRef.current = callback;

  const execute = useCallback(async () => {
    if (skip) return;

    try {
      setLoading(true);
      setHasBeenCalled(true);
      const result = await callbackRef.current();
      setData(result);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [skip]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
    setHasBeenCalled(false);
  }, []);

  useEffect(() => {
    if (hasBeenCalled || skip) return;
    execute();
  }, [execute, hasBeenCalled, skip, key]);

  useEffect(() => {
    if (lastKey == key) return;
    
    setLastKey(key);
    execute();

  }, [key, lastKey, execute]);

  return skip
    ? { data: null, error: null, loading: false, execute: () => {}, reset: () => {} }
    : { data, error, loading, refetch: execute, reset };
}
